import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { fetchVoiceTenantOverviews, fetchVoiceTenantOverviewSummary } from './voice-tenant-overview-api';

export const VOICE_OVERVIEW_PAGE_SIZE = 25;

export function useVoiceTenantOverviewSummary() {
  return useQuery({
    queryKey: ['voiceTenantOverviewSummary'],
    queryFn: fetchVoiceTenantOverviewSummary,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useVoiceTenantOverview() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});

  const cursor = pageCursors[currentPage];

  const overviewsQuery = useQuery({
    queryKey: ['voiceTenantOverviews', currentPage, cursor],
    queryFn: async () => ({
      ...(await fetchVoiceTenantOverviews({ cursor, limit: VOICE_OVERVIEW_PAGE_SIZE })),
      page: currentPage,
    }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const goToNextPage = useCallback(() => {
    const nextCursor = overviewsQuery.data?.cursor;
    if (overviewsQuery.data?.hasNextPage && nextCursor) {
      setPageCursors(prev => ({ ...prev, [currentPage + 1]: nextCursor }));
      setCurrentPage(page => page + 1);
    }
  }, [overviewsQuery.data, currentPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(page => page - 1);
    }
  }, [currentPage]);

  return {
    data: overviewsQuery.data?.data ?? [],
    isLoading: overviewsQuery.isLoading,
    isFetching: overviewsQuery.isFetching,
    error: overviewsQuery.error,
    hasNextPage: overviewsQuery.data?.hasNextPage ?? false,
    currentPage: overviewsQuery.data?.page ?? currentPage,
    goToNextPage,
    goToPreviousPage,
  };
}
